import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface User {
  id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isActive: boolean;
  isBlocked: boolean;
  blockReason?: string;
  blockedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, activationCode: string) => Promise<void>;
  register: (email: string, firstName: string, lastName: string, phoneNumber: string, activationCode: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored token on app load
    const storedToken = localStorage.getItem('quiz_token');
    if (storedToken) {
      setToken(storedToken);
      // Verify token and get user info
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      const userData = await api.getProfile(token);
      setUser(userData);
    } catch (error) {
      // Token is invalid, clear it
      localStorage.removeItem('quiz_token');
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, activationCode: string) => {
    try {
      const response = await api.login(email, activationCode);
      const { accessToken, refreshToken, user: userData } = response;
      
      setToken(accessToken);
      setUser(userData);
      localStorage.setItem('quiz_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('quiz_refresh_token', refreshToken);
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, firstName: string, lastName: string, phoneNumber: string, activationCode: string) => {
    try {
      const response = await api.register(email, firstName, lastName, phoneNumber, activationCode);
      const { accessToken, refreshToken, user: userData } = response;
      
      setToken(accessToken);
      setUser(userData);
      localStorage.setItem('quiz_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('quiz_refresh_token', refreshToken);
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('quiz_token');
    localStorage.removeItem('quiz_refresh_token');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
